import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import {
  Class as ClassIcon,
  Dashboard as DashboardIcon,
  BarChart as StatsIcon,
  Assignment as TasksIcon,
  PersonAdd as PersonAddIcon,
  Sms as SmsIcon,
  SwapHoriz as SubstituteIcon,
  People as PeopleIcon,
  Archive as ArchiveIcon,
  ExpandLess,
  ExpandMore,
  ManageAccounts as ManageAccountsIcon,
  Download as DownloadIcon,
  SupervisorAccount as SupervisorAccountIcon,
  AssignmentInd as AssignmentIndIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import madrasatiLightLogo from '../assets/madrasati-light.svg';
import madrasatiDarkLogo from '../assets/madrasati-dark.svg';

const drawerWidth = 240;

// Regular user menu items
const menuItems = [
  { id: 1, text: 'Dashboard', path: 'dashboard', icon: <StatsIcon /> },
  { id: 2, text: 'My Classes', path: 'classes', icon: <ClassIcon /> },
  { id: 3, text: 'My Tasks', path: 'tasks', icon: <TasksIcon /> },
  { id: 4, text: 'Substitutes', path: 'substitute-assignments', icon: <SubstituteIcon /> },
];

const SideMenu = ({ isOpen, toggleDrawer }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, userRole } = useAuth();
  const [manageUsersOpen, setManageUsersOpen] = useState(false);
  const [adminManagementOpen, setAdminManagementOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleNavigation = (path) => {
    // For regular menu items, use the teacher path format
    if (path.startsWith('dashboard') || path.startsWith('classes') || path.startsWith('tasks') || path.startsWith('substitute-assignments')) {
      navigate(`/teacher/${user?.id}/${path}`);
    } else {
      // For admin routes, use the direct path
      navigate(path);
    }
    // Close drawer on mobile after navigation
    if (isMobile) {
      toggleDrawer();
    }
  };
  
  // Function to check if an item is selected
  const isItemSelected = (path) => {
    if (path.startsWith('dashboard') || path.startsWith('classes') || path.startsWith('tasks') || path.startsWith('substitute-assignments')) {
      return location.pathname.includes(path);
    }
    return location.pathname === path;
  };

  // Check if any manage users sub-item is selected
  const isManageUsersSelected = () => {
    return isItemSelected('/admin/active') || 
           isItemSelected('/admin/archive') || 
           isItemSelected('/admin/pending');
  };

  // Check if any admin management sub-item is selected
  const isAdminManagementSelected = () => {
    return isItemSelected('/admin/management') || 
           isItemSelected('/admin/export') || 
           isItemSelected('/admin/teacher-activity') ||
           isItemSelected('/admin/substitute-management') ||
           isItemSelected('/admin/waitlist');
  };

  // Auto-expand Manage Users section if one of its children is selected
  useEffect(() => {
    if (isManageUsersSelected()) {
      setManageUsersOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-expand Admin Controls section if one of its children is selected
  useEffect(() => {
    if (isAdminManagementSelected()) {
      setAdminManagementOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle logo click - navigate to user's home
  const handleLogoClick = () => {
    if (userRole === 'admin' || userRole === 'super_admin') {
      navigate('/admin');
    } else {
      navigate(`/teacher/${user?.id}/dashboard`);
    }
    // Close drawer on mobile after navigation
    if (isMobile) {
      toggleDrawer();
    }
  };

  // Common styles for list items
  const listItemStyles = (selected) => ({
    cursor: 'pointer',
    color: theme.palette.secondary.contrastText,
    height: '56px',
    minHeight: '56px',
    py: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    borderLeft: selected ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
    transition: 'all 0.2s ease',
    '&.Mui-selected': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: 'white',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      borderLeft: !selected ? `4px solid rgba(52, 152, 219, 0.5)` : `4px solid ${theme.palette.primary.main}`,
    },
  });

  // Common styles for list item icons
  const listItemIconStyles = (selected) => ({
    color: selected ? theme.palette.primary.main : theme.palette.secondary.contrastText,
    minWidth: '40px',
    display: 'flex',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    transform: selected ? 'scale(1.1)' : 'scale(1)',
  });

  // Common styles for list item text
  const listItemTextStyles = (isOpen) => ({
    opacity: isOpen ? 1 : 0,
    transition: 'opacity 0.2s',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: isOpen ? 'block' : 'none',
    ml: 1,
  });

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? isOpen : true}
      onClose={isMobile ? toggleDrawer : undefined}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
      sx={{
        width: isMobile ? drawerWidth : (isOpen ? drawerWidth : 60),
        flexShrink: 0,
        overflowX: 'hidden',
        transition: 'width 0.3s ease',
        display: isMobile && !isOpen ? 'none' : 'block',
        '& .MuiDrawer-paper': {
          width: isMobile ? drawerWidth : (isOpen ? drawerWidth : 60),
          boxSizing: 'border-box',
          backgroundColor: theme.palette.secondary.main,
          borderRight: 'none',
          position: isMobile ? 'fixed' : 'fixed',
          top: 0,
          height: '100vh',
          overflow: 'hidden',
          transition: 'width 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          zIndex: isMobile ? 1200 : 1100,
        },
      }}
    >
      <Box sx={{ overflow: 'auto', marginTop: '72px' }}>
        <List>
          {/* Show admin menu items for admin users */}
          {(userRole === 'admin' || userRole === 'super_admin') ? (
            <>
              <ListItem
                button
                onClick={() => handleNavigation('/admin')}
                selected={isItemSelected('/admin')}
                sx={listItemStyles(isItemSelected('/admin'))}
              >
                <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin'))}>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Dashboard"
                  primaryTypographyProps={{
                    fontWeight: isItemSelected('/admin') ? 500 : 400,
                  }}
                  sx={listItemTextStyles(isOpen)}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => handleNavigation('/admin/classes')}
                selected={isItemSelected('/admin/classes')}
                sx={listItemStyles(isItemSelected('/admin/classes'))}
              >
                <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/classes'))}>
                  <ClassIcon />
                </ListItemIcon>
                <ListItemText
                  primary="All Classes"
                  primaryTypographyProps={{
                    fontWeight: isItemSelected('/admin/classes') ? 500 : 400,
                  }}
                  sx={listItemTextStyles(isOpen)}
                />
              </ListItem>
              
              {/* Admin Controls Section */}
              <ListItem
                button
                onClick={() => setAdminManagementOpen(!adminManagementOpen)}
                selected={isAdminManagementSelected()}
                sx={listItemStyles(isAdminManagementSelected())}
              >
                <ListItemIcon sx={listItemIconStyles(isAdminManagementSelected())}>
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Admin Controls"
                  primaryTypographyProps={{
                    fontWeight: isAdminManagementSelected() ? 500 : 400,
                  }}
                  sx={listItemTextStyles(isOpen)}
                />
                {isOpen && (adminManagementOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItem>
              <Collapse in={adminManagementOpen && isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin/management')}
                    selected={isItemSelected('/admin/management')}
                    sx={{
                      ...listItemStyles(isItemSelected('/admin/management')),
                      pl: isOpen ? 4 : 2,
                      minHeight: '48px',
                      height: '48px',
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/management'))}>
                      <AdminPanelSettingsIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Enrollment"
                      primaryTypographyProps={{
                        fontWeight: isItemSelected('/admin/management') ? 500 : 400,
                        fontSize: '0.9rem',
                      }}
                      sx={listItemTextStyles(isOpen)}
                    />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin/waitlist')}
                    selected={isItemSelected('/admin/waitlist')}
                    sx={{
                      ...listItemStyles(isItemSelected('/admin/waitlist')),
                      pl: isOpen ? 4 : 2,
                      minHeight: '48px',
                      height: '48px',
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/waitlist'))}>
                      <ListAltIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Waitlist"
                      primaryTypographyProps={{
                        fontWeight: isItemSelected('/admin/waitlist') ? 500 : 400,
                        fontSize: '0.9rem',
                      }}
                      sx={listItemTextStyles(isOpen)}
                    />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin/export')}
                    selected={isItemSelected('/admin/export')}
                    sx={{
                      ...listItemStyles(isItemSelected('/admin/export')),
                      pl: isOpen ? 4 : 2,
                      minHeight: '48px',
                      height: '48px',
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/export'))}>
                      <DownloadIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Data Export"
                      primaryTypographyProps={{
                        fontWeight: isItemSelected('/admin/export') ? 500 : 400,
                        fontSize: '0.9rem',
                      }}
                      sx={listItemTextStyles(isOpen)}
                    />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin/teacher-activity')}
                    selected={isItemSelected('/admin/teacher-activity')}
                    sx={{
                      ...listItemStyles(isItemSelected('/admin/teacher-activity')),
                      pl: isOpen ? 4 : 2,
                      minHeight: '48px',
                      height: '48px',
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/teacher-activity'))}>
                      <SupervisorAccountIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Teacher Activity"
                      primaryTypographyProps={{
                        fontWeight: isItemSelected('/admin/teacher-activity') ? 500 : 400,
                        fontSize: '0.9rem',
                      }}
                      sx={listItemTextStyles(isOpen)}
                    />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin/substitute-management')}
                    selected={isItemSelected('/admin/substitute-management')}
                    sx={{
                      ...listItemStyles(isItemSelected('/admin/substitute-management')),
                      pl: isOpen ? 4 : 2,
                      minHeight: '48px',
                      height: '48px',
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/substitute-management'))}>
                      <AssignmentIndIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Substitutes"
                      primaryTypographyProps={{
                        fontWeight: isItemSelected('/admin/substitute-management') ? 500 : 400,
                        fontSize: '0.9rem',
                      }}
                      sx={listItemTextStyles(isOpen)}
                    />
                  </ListItemButton>
                </List>
              </Collapse>
              
              {/* Manage Users Section */}
              <ListItem
                button
                onClick={() => setManageUsersOpen(!manageUsersOpen)}
                selected={isManageUsersSelected()}
                sx={listItemStyles(isManageUsersSelected())}
              >
                <ListItemIcon sx={listItemIconStyles(isManageUsersSelected())}>
                  <ManageAccountsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Manage Users"
                  primaryTypographyProps={{
                    fontWeight: isManageUsersSelected() ? 500 : 400,
                  }}
                  sx={listItemTextStyles(isOpen)}
                />
                {isOpen && (manageUsersOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItem>
              <Collapse in={manageUsersOpen && isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin/active')}
                    selected={isItemSelected('/admin/active')}
                    sx={{
                      ...listItemStyles(isItemSelected('/admin/active')),
                      pl: isOpen ? 4 : 2,
                      minHeight: '48px',
                      height: '48px',
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/active'))}>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Active Users"
                      primaryTypographyProps={{
                        fontWeight: isItemSelected('/admin/active') ? 500 : 400,
                        fontSize: '0.9rem',
                      }}
                      sx={listItemTextStyles(isOpen)}
                    />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin/archive')}
                    selected={isItemSelected('/admin/archive')}
                    sx={{
                      ...listItemStyles(isItemSelected('/admin/archive')),
                      pl: isOpen ? 4 : 2,
                      minHeight: '48px',
                      height: '48px',
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/archive'))}>
                      <ArchiveIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Archived Users"
                      primaryTypographyProps={{
                        fontWeight: isItemSelected('/admin/archive') ? 500 : 400,
                        fontSize: '0.9rem',
                      }}
                      sx={listItemTextStyles(isOpen)}
                    />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin/pending')}
                    selected={isItemSelected('/admin/pending')}
                    sx={{
                      ...listItemStyles(isItemSelected('/admin/pending')),
                      pl: isOpen ? 4 : 2,
                      minHeight: '48px',
                      height: '48px',
                    }}
                  >
                    <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/pending'))}>
                      <PersonAddIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Pending Users"
                      primaryTypographyProps={{
                        fontWeight: isItemSelected('/admin/pending') ? 500 : 400,
                        fontSize: '0.9rem',
                      }}
                      sx={listItemTextStyles(isOpen)}
                    />
                  </ListItemButton>
                </List>
              </Collapse>
              <ListItem
                button
                onClick={() => handleNavigation('/admin/sms')}
                selected={isItemSelected('/admin/sms')}
                sx={listItemStyles(isItemSelected('/admin/sms'))}
              >
                <ListItemIcon sx={listItemIconStyles(isItemSelected('/admin/sms'))}>
                  <SmsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Send SMS"
                  primaryTypographyProps={{
                    fontWeight: isItemSelected('/admin/sms') ? 500 : 400,
                  }}
                  sx={listItemTextStyles(isOpen)}
                />
              </ListItem>
            </>
          ) : (
            // Show regular menu items for non-admin users
            menuItems.map((item) => {
              const selected = isItemSelected(item.path);
              return (
                <ListItem
                  button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  selected={selected}
                  sx={listItemStyles(selected)}
                >
                  <ListItemIcon sx={listItemIconStyles(selected)}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: selected ? 500 : 400,
                    }}
                    sx={listItemTextStyles(isOpen)}
                  />
                </ListItem>
              );
            })
          )}
        </List>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 2,
          paddingBottom: 3,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box
          component="img"
          src={theme.palette.mode === 'dark' ? madrasatiDarkLogo : madrasatiLightLogo}
          alt="Madrasati Logo"
          onClick={handleLogoClick}
          sx={{
            width: isOpen ? '140px' : '40px',
            height: 'auto',
            transition: 'width 0.3s ease',
            opacity: 0.9,
            cursor: 'pointer',
            '&:hover': {
              opacity: 1,
            },
          }}
        />
      </Box>
    </Drawer>
  );
};

export default SideMenu;
